<template>
  <div
    v-if="reportStore.activeTooltipValue === props.value"
    class="i-onboarding-tooltip"
    :class="{ low: props.showBottomTip, left: props.positionLeft }"
  >
    <img
      v-if="!props.showBottomTip"
      class="tippy"
      :src="IconTippyBlue"
    />
    <div class="main">
      <img
        class="close"
        :src="IconClose"
        @click.prevent="reportStore.closeTooltip()"
      />
      <div class="details">
        <div class="caption">{{ tooltipContent[props.value].caption }}</div>
        <div class="text">{{ tooltipContent[props.value].text }}</div>
      </div>
      <div class="nav">
        <div class="count">
          {{ (reportStore.activeTooltipIndex || 0) + 1 }} of {{ reportStore.calculatedTooltipIndexes.length }}
        </div>
        <div class="buttons">
          <iBaseButton
            v-if="reportStore.activeTooltipIndex !== 0"
            v-bind="BUTTON_STYLE.previous"
            text="Previous"
            @click.prevent="action(false)"
          />
          <iBaseButton
            v-bind="BUTTON_STYLE.next"
            :text="ctaText"
            @click.prevent="action(true)"
          />
        </div>
      </div>
    </div>
    <img
      v-if="props.showBottomTip"
      class="tippy"
      :src="IconTippyBlue"
    />
  </div>
</template>

<script setup lang="ts">
import { useReportStore } from "@/stores/report"
import { azureRadiance800, black, white } from "@/styles/colors"
import { computed, nextTick, reactive } from "vue"
import { useRouter } from "vue-router"

import { GlobalVariables } from "@/helpers/globalVariables"

import iBaseButton from "@/components/base/BaseButton.vue"

// ASSETS
import IconClose from "@/assets/icons/icon-close-whiite.svg"
import IconTippyBlue from "@/assets/icons/icon-tippy-blue.svg"

// TYPES
import type { OnboardingTooltipContentType, OnboardingTooltipValueType } from "@/types/onboardingTooltips"

// STORES AND ROUTERS
const reportStore = useReportStore()
const router = useRouter()

// PROPS
const props = defineProps({
  value: {
    type: Number as () => OnboardingTooltipValueType,
    default: 1
  },
  positionLeft: {
    type: Boolean,
    default: false
  },
  showBottomTip: {
    type: Boolean,
    default: false
  }
})

// CONSTANTS
const REPORT_ROUTES = GlobalVariables.urls.report.children
const SHARED_BUTTON_STYLE = {
  size: "small",
  fontWeight: "500",
  width: "fit-content",
  borderRadius: "5px",
  padding: "0px 20px"
}
const BUTTON_STYLE = {
  previous: {
    ...SHARED_BUTTON_STYLE,
    backgroundColor: azureRadiance800,
    textColor: white,
    version: "outline"
  },
  next: {
    ...SHARED_BUTTON_STYLE,
    backgroundColor: white,
    textColor: black
  }
}

// DATA
const tooltipContent: OnboardingTooltipContentType = reactive({
  1: {
    caption: "Navigate between sections",
    text: "Easily navigate to different sections of your report using our navigation."
  },
  2: {
    caption: "Navigate to your top choice",
    text: "Navigate to your top career recommendation.",
    nextAction: () => {
      router.push({ name: REPORT_ROUTES.home.routeName })
    }
  },
  3: {
    caption: "Compare different careers",
    text: "Compare potential earnings across various recommended careers.",
    nextAction: () => {
      router.push({ name: REPORT_ROUTES.home.routeName })
    },
    previousAction: () => {
      router.push({ name: REPORT_ROUTES.home.routeName })
    }
  },
  4: {
    caption: "Favorite, delete, or replace",
    text: "Select what you love, delete what you don’t, and swap out any options that don’t suit you.",
    nextAction: () => {
      router.push({ name: REPORT_ROUTES.careersOverview.routeName })
    },
    previousAction: () => {
      router.push({ name: REPORT_ROUTES.careersOverview.routeName })
    }
  },
  5: {
    caption: "Save to PDF",
    text: "Download a PDF version of your results to save or share whenever you like.",
    nextAction: () => {
      router.push({ name: REPORT_ROUTES.careers.routeName, params: { jobId: reportStore.featuredJobId } })
    },
    previousAction: () => {
      router.push({ name: REPORT_ROUTES.careers.routeName, params: { jobId: reportStore.featuredJobId } })
    }
  },
  6: {
    caption: "Explore Curated Resources",
    text: "Access hand-picked resources tailored to help you grow and succeed.",
    nextAction: () => {
      router.push({ name: REPORT_ROUTES.resources.routeName })
    }
  }
})

// COMPUTED
const ctaText = computed(() =>
  reportStore.calculatedTooltipIndexes.length - 1 === reportStore.activeTooltipIndex ? "Finish" : "Next"
)

// FUNCTIONS
const scrollToOnboardingTooltip = () => {
  nextTick(() => {
    const onboardingTooltip = document.querySelector(".i-onboarding-tooltip")

    const offset = 206
    const element = onboardingTooltip

    if (element) {
      const elementRect = element.getBoundingClientRect()
      const isNotVisible =
        elementRect.top <= document.body.scrollTop ||
        elementRect.bottom >= (window.innerHeight || document.documentElement.clientHeight)

      // Only scroll to OnboardingTooltip if it is not visible within the viewport
      if (isNotVisible) {
        const elementPosition = element.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.scrollY - offset

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        })
      }
    }
  })
}

const action = (isNext: boolean) => {
  if (isNext) {
    reportStore.nextTooltipIndex()
  } else {
    reportStore.previousTooltipIndex()
  }
  if (reportStore.activeTooltipValue)
    tooltipContent[reportStore.activeTooltipValue][isNext ? "nextAction" : "previousAction"]?.()
  else {
    router.push({ name: REPORT_ROUTES.home.routeName })
  }
  scrollToOnboardingTooltip()
}
</script>

<style scoped lang="scss">
.i-onboarding-tooltip {
  position: absolute;
  top: 100%;
  z-index: 500;
  width: 300px;
  color: $white;

  .main {
    position: relative;
    background-color: $azure-radiance800;
    box-shadow: 0 0 15px 5px rgb(0 0 0 / 20%);

    .close {
      position: absolute;
      right: 12px;
      top: 12px;
      cursor: pointer;
    }

    .details {
      border-bottom: 1px solid $azure-radiance700;
      padding: 12px 18px 12px 22px;
      text-align: start;

      @include flex(column, start, start, $gap: 4px);

      .caption {
        font-weight: 700;

        @include text-md;
      }

      .text {
        font-weight: 400;

        @include text-sm;
      }
    }

    .nav {
      @include flex(row, space-between, center, $gap: 10px);

      padding: 12px 18px 12px 22px;

      .buttons {
        @include flex(row, center, center, $gap: 6px);
      }
    }
  }

  .tippy {
    vertical-align: top;
    transform: translate(100%, 2px) scaleY(-1);
    position: relative;
    z-index: 999;
  }

  &.low {
    top: unset;
    bottom: 100%;

    .tippy {
      transform: translate(100%, -1px);
    }
  }

  &.left {
    transform: translate(-70%, 0);

    @media screen and (min-width: $breakpoint-md) {
      transform: translate(-50%, 0);
    }

    .tippy {
      transform: translate(350%, 2px) scaleX(-1) scaleY(-1);
    }
  }
}
</style>
